import React, { useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { RadioButton } from 'primereact/radiobutton';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { useDeleteDealerAddressPriceListMutation } from '../../../../../api/endpoints/priceList';
import DealerDropdown from '../../../../../components/common/DealerDropdown';
import DealerAddressDropdown from '../../../../../components/common/DealerAddressDropdown';
import PriceListDropdown from '../../../../../components/common/PriceListDropdown';
import { useLanguage } from '../../../../../contexts/LanguageContext';

const CustomerPriceListForm = ({
    visible,
    onHide,
    submitted,
    editMode,
    selectedCustomer,
    setSelectedCustomer,
    selectedAddress,
    setSelectedAddress,
    selectedPriceList,
    setSelectedPriceList,
    usageType,
    setUsageType,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    handleSubmit,
    currentAddressPrice,
    toast,
    refetch,
    isLoadingEditItem
}) => {
    const { th } = useLanguage();
    const [deleteAddressPrice] = useDeleteDealerAddressPriceListMutation();

    useEffect(() => {
        if (editMode && currentAddressPrice && visible) {
            const dealerForDropdown = {
                id: currentAddressPrice.dealerId,
                dealerId: currentAddressPrice.dealerId,
                name: currentAddressPrice.dealerName,
                displayValue: currentAddressPrice.dealerName
            };
            setSelectedCustomer(dealerForDropdown);

            setTimeout(() => {
                if (currentAddressPrice.dealerAddressId) {
                    setSelectedAddress(currentAddressPrice.dealerAddressId);
                }
            }, 300);
        }
    }, [editMode, currentAddressPrice, visible]);

    const handleDealerChange = (dealer) => {
        if (dealer && typeof dealer.id !== 'undefined') {
            const normalizedDealer = {
                ...dealer,
                id: Number(dealer.id),
                dealerId: Number(dealer.id)
            };
            setSelectedCustomer(normalizedDealer);
        } else {
            setSelectedCustomer(null);
        }
        setSelectedAddress(null);
    };

    const handleAddressChange = (address) => {
        setSelectedAddress(address);
    };

    const handleDelete = () => {
        confirmDialog({
            message: th('customerAddressPrice.deleteConfirmMessage'),
            header: th('customerAddressPrice.deleteConfirmHeader'),
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    await deleteAddressPrice(currentAddressPrice.id).unwrap();
                    toast?.current?.show({
                        severity: 'success',
                        summary: th('customerAddressPrice.deleted'),
                        detail: th('customerAddressPrice.deleteSuccessMessage'),
                        life: 3000
                    });
                    onHide();
                    refetch?.();
                } catch (error) {
                    console.error('Silme hatası:', error);
                    toast?.current?.show({
                        severity: 'error',
                        summary: th('common.error'),
                        detail: th('customerAddressPrice.deleteErrorMessage'),
                        life: 3000
                    });
                }
            },
            reject: () => {},
            acceptLabel: th('common.yes'),
            rejectLabel: th('common.no'),
            acceptClassName: 'p-button-danger',
            rejectClassName: 'p-button-outlined'
        });
    };

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={editMode ? th('customerAddressPrice.editPriceList') : th('customerAddressPrice.newPriceList')}
            modal
            className="p-fluid"
            style={{ width: '550px' }}
            footer={
                <div className="flex justify-content-between">
                    <div>{editMode && <Button label={th('common.delete')} icon="pi pi-trash" severity="danger" outlined onClick={handleDelete} />}</div>
                    <div className="flex gap-2">
                        <Button label={th('common.cancel')} icon="pi pi-times" outlined onClick={onHide} />
                        <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} />
                    </div>
                </div>
            }
        >
            {editMode && isLoadingEditItem ? (
                <div className="text-center p-4">
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                    <p className="mt-2">{th('common.loading')}</p>
                </div>
            ) : (
                <>
                    <div className="field mb-4">
                        <label className="font-bold block mb-2">{th('customerAddressPrice.dealer')}</label>
                        <DealerDropdown
                            value={selectedCustomer?.id?.toString() || selectedCustomer?.dealerId?.toString()}
                            onChange={handleDealerChange}
                            className={submitted && !selectedCustomer ? 'p-invalid' : ''}
                            placeholder={th('customerAddressPrice.dealerPlaceholder')}
                        />
                        {submitted && !selectedCustomer && <small className="p-error">{th('customerAddressPrice.dealerRequired')}</small>}
                    </div>

                    <div className="field mb-4">
                        <label className="font-bold block mb-2">{th('customerAddressPrice.address')}</label>
                        <DealerAddressDropdown
                            value={selectedAddress}
                            onChange={handleAddressChange}
                            dealerId={Number(selectedCustomer?.dealerId || selectedCustomer?.id)}
                            disabled={!selectedCustomer}
                            className={submitted && !selectedAddress ? 'p-invalid' : ''}
                        />
                        {submitted && !selectedAddress && <small className="p-error">{th('customerAddressPrice.addressRequired')}</small>}
                    </div>

                    <div className="field mb-4">
                        <label className="font-bold block mb-2">{th('customerAddressPrice.priceList')}</label>
                        <PriceListDropdown value={selectedPriceList} onChange={(value) => setSelectedPriceList(value)} className={submitted && !selectedPriceList ? 'p-invalid' : ''} placeholder={th('customerAddressPrice.priceListPlaceholder')} />
                        {submitted && !selectedPriceList && <small className="p-error">{th('customerAddressPrice.priceListRequired')}</small>}
                    </div>

                    <div className="field mb-4">
                        <label className="font-bold block mb-2">{th('customerAddressPrice.usageType')}</label>
                        <div className="flex align-items-center gap-4">
                            <div>
                                <RadioButton inputId="usageService" name="usageType" value={1} onChange={(e) => setUsageType(e.value)} checked={usageType === 1} />
                                <label htmlFor="usageService" className="ml-2">
                                    {th('customerAddressPrice.service')}
                                </label>
                            </div>
                            <div>
                                <RadioButton inputId="usageSales" name="usageType" value={2} onChange={(e) => setUsageType(e.value)} checked={usageType === 2} />
                                <label htmlFor="usageSales" className="ml-2">
                                    {th('customerAddressPrice.sales')}
                                </label>
                            </div>
                        </div>
                        {submitted && usageType === null && <small className="p-error">{th('customerAddressPrice.usageTypeRequired')}</small>}
                    </div>

                    <div className="field mb-4">
                        <label className="font-bold block mb-2">{th('customerAddressPrice.startDate')}</label>
                        <Calendar value={startDate} onChange={(e) => setStartDate(e.value)} dateFormat="dd/mm/yy" showIcon placeholder={th('customerAddressPrice.startDatePlaceholder')} className={submitted && !startDate ? 'p-invalid' : ''} />
                        {submitted && !startDate && <small className="p-error">{th('customerAddressPrice.startDateRequired')}</small>}
                    </div>

                    <div className="field mb-4">
                        <label className="font-bold block mb-2">{th('customerAddressPrice.endDate')}</label>
                        <Calendar value={endDate} onChange={(e) => setEndDate(e.value)} dateFormat="dd/mm/yy" showIcon placeholder={th('customerAddressPrice.endDatePlaceholder')} minDate={startDate} />
                    </div>
                </>
            )}
        </Dialog>
    );
};

export default CustomerPriceListForm;
